import { CalendarDays } from 'lucide-react';
import type { ExamAssignmentScope } from '../../school_panel/api/types';
import { withTime } from '../../school_panel/lib/dates';

interface Props {
  dueDate: string | null;
  dueAt?: string | null;
  assignmentScope: ExamAssignmentScope;
  assignedStudentCount?: number;
  sections?: { id: number; name: string }[];
  instructions?: string;
  onEdit?: () => void;
  gradeName?: string | null;
  subjectName?: string | null;
}

function localTodayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dueDateBadge(dueDate: string | null, dueAt?: string | null): JSX.Element | null {
  if (!dueDate && !dueAt) return null;
  const isPast = dueAt ? new Date(dueAt) < new Date() : (dueDate as string) < localTodayIso();
  const text = dueAt
    ? withTime(dueAt, { weekday: 'short', day: 'numeric', month: 'short' })
    : dueDate;
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
        isPast ? 'bg-ground text-muted' : 'bg-accent-bg text-accent-ink'
      }`}
    >
      <CalendarDays size={14} strokeWidth={1.8} aria-hidden="true" className="mr-1.5" />Due {text}
    </span>
  );
}

export function scopeLabel(
  scope: ExamAssignmentScope,
  gradeName: string | null | undefined,
  subjectName: string | null | undefined,
  count: number,
): string {
  switch (scope) {
    case 'grade':
      return `All ${gradeName ?? 'grade'} students`;
    case 'grade_subject':
      return `${gradeName ?? 'Grade'}${subjectName ? ` · ${subjectName}` : ''} students`;
    case 'individual':
      return `${count} student${count === 1 ? '' : 's'}`;
    default:
      return '';
  }
}

export default function ExamAssignmentInfo({
  dueDate,
  dueAt,
  assignmentScope,
  assignedStudentCount = 0,
  sections,
  instructions,
  onEdit,
  gradeName,
  subjectName,
}: Props) {
  const label = scopeLabel(assignmentScope, gradeName, subjectName, assignedStudentCount);
  const due = dueDateBadge(dueDate, dueAt);
  const sectionNames = (sections ?? []).map((x) => x.name).join(', ');
  if (!label && !due && !onEdit) return null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {due}
        {label && <span className="inline-flex items-center rounded-full bg-mist px-2.5 py-1 text-xs font-semibold text-ink">
          {label}
        </span>}
        {sectionNames && <span className="inline-flex items-center rounded-full bg-mist px-2.5 py-1 text-xs font-semibold text-ink">
          Sections: {sectionNames}
        </span>}
        {onEdit && (
          <button type="button" onClick={onEdit} className="inline-flex min-h-[32px] items-center text-[13px] font-semibold text-muted-2 underline-offset-[3px] hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            Edit assignment
          </button>
        )}
      </div>
      {instructions && <p className="mt-3.5 max-w-[68ch] whitespace-pre-line text-sm text-muted-2">{instructions}</p>}
    </div>
  );
}
